import React from 'react';
import { Box, Badge } from '@mui/material';
import Logo from './../../assets/images/Home/Logo.png'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import SideMenu from './SideMenu';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { useCartSummary } from '../../hooks/shop/useCart';
import Search from '../../assets/icons/NavbarIcon/Icon-1.png'
import Cart from '../../assets/icons/NavbarIcon/Icon.png'
import MessageIcon from '../../assets/icons/NavbarIcon/Message Icon.png';

const MobileNavbar = ({ isAuthenticated, setIsAuthenticated, isApproved }) => {
  const unreadCount = useUnreadCount(isAuthenticated && isApproved);
  const { data: cartSummary } = useCartSummary({ enabled: !!(isAuthenticated && isApproved) });
  const cartCount = cartSummary?.cnt || 0;
  const navigate = useNavigate();
  const location = useLocation();
  const [isSearchExpanded, setIsSearchExpanded] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    if (location.pathname === '/search') {
      setSearchValue(q);
    } else {
      setSearchValue('');
      setIsSearchExpanded(false);
    }
  }, [location]);

  const handleSearchSubmit = () => {
    if (searchValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
      setIsSearchExpanded(false);
      setSearchValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  return (
    <>
      <Box sx={{
        width:"100%",
        height:"10vh",
        backgroundColor:"#fff",
        display:{md:"none",xs:"flex"},
        alignItems:"center",
        position:"fixed",
        boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px', // Add box shadow
        zIndex:"10",
        px: "15px",
        boxSizing: 'border-box'
      }} >
        {isSearchExpanded ? (
          // Expanded Search View
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: '15px' }}>
            {/* Logo on the left (with extra spacing to prevent "getting slapped") */}
            <Box sx={{ width: "45px", height: "45px", flexShrink: 0, mr: '5px' }}>
              <Link to="/" style={{ display: 'block', width: '100%', height: '100%' }}>
                <LazyLoadImage 
                  src={Logo} 
                  effect="blur"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Link>
            </Box>

            {/* Expanding Search Input Bar */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              bgcolor: '#FFF', 
              border: '1px solid #E5BE80', 
              borderRadius: '100px', 
              pl: '15px',
              pr: '12px',
              py: '4px',
              height: '40px',
              boxSizing: 'border-box'
            }}>
              <input
                type="text"
                placeholder="Search Products..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={handleKeyPress}
                autoFocus
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  width: '100%',
                  fontSize: '14px',
                  color: '#333'
                }}
              />
              {/* Search Icon at the RIGHT side INSIDE the search bar */}
              <Box 
                onClick={handleSearchSubmit}
                sx={{ 
                  width: '18px', 
                  height: '18px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  cursor: 'pointer', 
                  ml: 1,
                  flexShrink: 0 
                }}
              >
                <LazyLoadImage src={Search} effect="blur" width="100%" height="100%" />
              </Box>
            </Box>

            {/* Close / Collapse Button OUTSIDE the search bar */}
            <Box 
              onClick={() => {
                setIsSearchExpanded(false);
                setSearchValue('');
              }}
              sx={{ 
                color: '#666', 
                cursor: 'pointer', 
                fontSize: '20px', 
                fontWeight: 'bold',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pl: '5px',
                pr: '5px'
              }}
            >
              ✕
            </Box>
          </Box>
        ) : (
          // Standard View (Logo at center)
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%', position: 'relative' }}>
            {/* Side Menu on Left */}
            <Box sx={{
                width:"40px",
                height:"40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: 'absolute',
                left: 0
            }} >
              <SideMenu isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} isApproved={isApproved} />
            </Box>

            {/* Logo Centered Absolutely */}
            <Box sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width:"45px",
                height:"45px",
                display:"flex",
                alignItems:"center",
                justifyContent:"center"
            }} >
              <Box sx={{ width:"45px", height:"45px" }} >
                <Link to="/" style={{ display: 'block', width: '100%', height: '100%' }}>
                  <LazyLoadImage 
                    src={Logo} 
                    effect="blur"
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                </Link>
              </Box>
            </Box>

            {/* Action Icons on Right */}
            <Box sx={{
                display:"flex",
                alignItems:"center",
                justifyContent:"end",
                position: 'absolute',
                right: 0,
                gap: '15px'
            }} >
              {isAuthenticated && isApproved && (
                <>
                  {/* Search Icon (Click to Expand) */}
                  <Box 
                    onClick={() => setIsSearchExpanded(true)}
                    sx={{
                        width:"22px",
                        height:"22px",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                    }}
                  >
                    <LazyLoadImage 
                      src={Search} 
                      effect="blur"
                      width="100%"
                      height="100%"
                    />
                  </Box>
                </>
              )}

              {isAuthenticated && isApproved && (
                <>
                  {/* Cart Icon */}
                  <Box 
                    sx={{
                        width:"22px",
                        height:"22px",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                  >
                    <Link to="/Cart" style={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
                      <Badge 
                        badgeContent={cartCount} 
                        max={99}
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: 'rgba(105, 83, 55, 1)',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.6rem',
                            height: '14px',
                            minWidth: '14px',
                            padding: '0 2px',
                          }
                        }}
                      >
                        <LazyLoadImage 
                          src={Cart} 
                          effect="blur"
                          width="100%"
                          height="100%"
                          style={{ display: 'block' }}
                        />
                      </Badge>
                    </Link>
                  </Box>

                  {/* Chat Icon (Corrected Size) */}
                  <Box 
                    sx={{
                        width:"22px",
                        height:"22px",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                  >
                    <Link to="/chat" style={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
                      <Badge 
                        badgeContent={unreadCount} 
                        max={99}
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: '#695337',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.6rem',
                            height: '14px',
                            minWidth: '14px',
                            padding: '0 2px',
                          }
                        }}
                      >
                        <img 
                          src={MessageIcon} 
                          alt="" 
                          style={{ width: '22px', height: '22px', display: 'block' }} 
                        />
                      </Badge>
                    </Link>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{
          width:"100%",
          height:"10vh",
          display:{xs:"flex",md:"none"}
      }} >
      </Box>
    </>
  )
}

export default MobileNavbar
